import { z } from "zod";

/** The model's first look at a repository (the code reading round, 2026-11-04; brief part four, the red team's finding 18) and the line
 *  under the repository on Knowledge › Sources (part six). The map: the folders to two levels with their file counts and sizes, the
 *  top-level files, the README's first 40 lines, at most 120 lines; a release whose files fit lists them (every file down to the second
 *  folder level, deeper ones counted under their folder). The outline (built with the local copy) adds one line per file of the names
 *  simple patterns find, at most 400 lines, the biggest files first. Pure. */

export const CODE_MAP_LINES = 120;
export const CODE_OUTLINE_LINES = 400;
const README_LINES = 40;
const NAMES_PER_FILE = 30;

export type CodeMapFile = { path: string; size: number };
export type CodeOutlineFile = { path: string; size: number; names: string[] };
export type CodeMapInput = { kept: readonly CodeMapFile[]; readme?: string | null; partial?: { folders: string[] } | null; outline?: readonly CodeOutlineFile[] };

/** a size in words: whole MB from 1 MB, else whole KB (at least 1) */
export function codeSize(bytes: number): string {
  const MB = 1024 * 1024;
  return bytes >= MB ? `${Math.round(bytes / MB)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}
const count = (n: number) => n.toLocaleString("en-US");
const filesWord = (n: number) => `${count(n)} ${n === 1 ? "file" : "files"}`;

type Folder = { path: string; level: 1 | 2; files: number; bytes: number };
/** every folder of the first two levels with the files and bytes under it, whatever their depth */
function foldersOf(kept: readonly CodeMapFile[]): Folder[] {
  const map = new Map<string, Folder>();
  for (const f of kept) {
    const parts = f.path.split("/");
    for (let level = 1; level <= 2 && level < parts.length; level++) {
      const path = `${parts.slice(0, level).join("/")}/`;
      const x = map.get(path) ?? { path, level: level as 1 | 2, files: 0, bytes: 0 };
      x.files += 1; x.bytes += f.size; map.set(path, x);
    }
  }
  return [...map.values()];
}
const folderLine = (f: Folder) => `${f.path} (${filesWord(f.files)}, ${codeSize(f.bytes)})`;
const depthOf = (path: string) => path.split("/").length - 1;

/** the detailed map: every folder of two levels in name order, each followed by its files down to that level */
function detailed(kept: readonly CodeMapFile[], folders: Folder[]): string[] {
  const shown = kept.filter((f) => depthOf(f.path) <= 2).map((f) => f.path);
  return [...shown, ...folders.map((f) => f.path)].sort().map((p) => (p.endsWith("/") ? folderLine(folders.find((f) => f.path === p)!) : p));
}
/** the folders' map: the top-level files, then the first-level folders, the biggest first, each with its own second-level ones, cut at `room` */
function overview(kept: readonly CodeMapFile[], folders: Folder[], room: number): string[] {
  const top = kept.filter((f) => !f.path.includes("/")).map((f) => f.path).sort();
  const bigFirst = (a: Folder, b: Folder) => b.files - a.files || b.bytes - a.bytes || a.path.localeCompare(b.path);
  const ordered = folders.filter((f) => f.level === 1).sort(bigFirst).flatMap((f) => [f, ...folders.filter((s) => s.level === 2 && s.path.startsWith(f.path)).sort(bigFirst)]);
  const lines = [...top.slice(0, Math.max(0, room - 1)), ...ordered.map(folderLine)];
  if (lines.length <= room) return lines;
  const kept1 = lines.slice(0, room - 1);
  return [...kept1, `… and ${lines.length - kept1.length} more folders and files; list a folder to see them`];
}
function readmeBlock(readme: string | null | undefined): string[] {
  if (!readme?.trim()) return [];
  const lines = readme.replace(/\r\n?/g, "\n").split("\n").slice(0, README_LINES);
  return ["The README's first lines:", ...lines.map((l) => `  ${l}`.trimEnd())];
}
function partialLine(partial: CodeMapInput["partial"]): string[] {
  if (!partial) return [];
  const names = partial.folders.slice(0, 20);
  return [`This list is partial: GitHub cut the tree short; not listed${names.length ? `: ${names.join(", ")}${partial.folders.length > names.length ? ", …" : ""}` : ""} (list_files on a folder reads it).`];
}
function outlineBlock(outline: readonly CodeOutlineFile[] | undefined): string[] {
  const rows = [...(outline ?? [])].filter((o) => o.names.length).sort((a, b) => b.size - a.size || a.path.localeCompare(b.path)).slice(0, CODE_OUTLINE_LINES);
  return rows.length ? ["Outline (the names in each file, the biggest files first):", ...rows.map((o) => `${o.path}: ${o.names.slice(0, NAMES_PER_FILE).join(", ")}`)] : [];
}

/** The map of the kept files (see the module's comment): at most 120 lines before the outline, at most 400 outline lines. */
export function codeMap(o: CodeMapInput): string {
  const head = [...partialLine(o.partial), ...readmeBlock(o.readme)];
  const room = Math.max(10, CODE_MAP_LINES - head.length - (head.length ? 1 : 0));
  const folders = foldersOf(o.kept);
  const full = detailed(o.kept, folders);
  const body = full.length <= room ? full : overview(o.kept, folders, room);
  const outline = outlineBlock(o.outline);
  return [...head, ...(head.length ? [""] : []), ...body, ...(outline.length ? ["", ...outline] : [])].join("\n");
}

/* ---------- the outline: names by simple patterns per language (Q8: patterns, not a parser) ---------- */
const PATTERNS: [RegExp, RegExp[]][] = [
  [/\.(php|phtml|inc)$/i, [/\bfunction\s+&?([A-Za-z_]\w*)\s*\(/g, /\b(?:class|interface|trait|enum)\s+([A-Za-z_]\w*)/g, /\b(?:add_action|add_filter|do_action|apply_filters|add_shortcode)\s*\(\s*['"]([^'"]+)['"]/g]],
  [/\.(m?[jt]sx?|cjs|vue|svelte)$/i, [/\bfunction\s*\*?\s+([A-Za-z_$][\w$]*)\s*\(/g, /\bclass\s+([A-Za-z_$][\w$]*)/g, /\b(?:const|let|var)\s+([A-Za-z_$][\w$]*)\s*=\s*(?:async\s+)?(?:function\b|\([^)]*\)\s*=>|[A-Za-z_$][\w$]*\s*=>)/g, /\b(?:interface|type|enum)\s+([A-Z][\w$]*)/g]],
  [/\.(css|scss|sass|less)$/i, [/(\.-?[A-Za-z_][\w-]*)(?=[^{};]*\{)/g]],
  [/\.py$/i, [/^\s*(?:async\s+)?def\s+([A-Za-z_]\w*)/gm, /^\s*class\s+([A-Za-z_]\w*)/gm]],
  [/\.rb$/i, [/^\s*def\s+(?:self\.)?([A-Za-z_]\w*[?!=]?)/gm, /^\s*(?:class|module)\s+([A-Z]\w*(?:::\w+)*)/gm]],
  [/\.go$/i, [/^func\s+(?:\([^)]*\)\s*)?([A-Za-z_]\w*)/gm, /^type\s+([A-Za-z_]\w*)/gm]],
  [/\.(java|kt|cs|scala|swift)$/i, [/\b(?:class|interface|enum|record|struct|object)\s+([A-Za-z_]\w*)/g, /^\s*(?:(?:public|private|protected|internal|static|final|override|abstract|async|fun|func|def)\s+)+[\w<>\[\],.?\s]*?\b([A-Za-z_]\w*)\s*\(/gm]],
  [/\.(c|h|cc|cpp|hpp|rs)$/i, [/^(?:[A-Za-z_][\w\s*&:<>,]*?\s+\**)?([A-Za-z_]\w*)\s*\([^;{]*\)\s*\{/gm, /\b(?:struct|class|enum|union|trait|impl)\s+([A-Za-z_]\w*)/g, /\bfn\s+([A-Za-z_]\w*)/g]],
];
const NOT_NAMES = new Set(["if", "for", "while", "switch", "return", "catch", "function", "else", "new"]);
/** the functions, classes, hooks and selectors a file names, in order, each once (an unknown language: none) */
export function outlineOf(path: string, text: string): string[] {
  const res = PATTERNS.find(([ext]) => ext.test(path))?.[1];
  if (!res) return [];
  const found: { at: number; name: string }[] = [];
  for (const re of res) for (const m of text.matchAll(new RegExp(re.source, re.flags))) if (m[1] && !NOT_NAMES.has(m[1])) found.push({ at: m.index ?? 0, name: m[1] });
  return [...new Set(found.sort((a, b) => a.at - b.at).map((f) => f.name))].slice(0, NAMES_PER_FILE * 2);
}

/* ---------- the Sources row's line (part six) ---------- */
export const CodeReadSummarySchema = z.object({
  version: z.string(), from: z.enum(["endpoint", "tag", "release", "branch"]), keptFiles: z.number().int().min(0), keptBytes: z.number().min(0),
  skippedFiles: z.number().int().min(0), skipped: z.array(z.object({ reason: z.string(), files: z.number().int().min(0) }).strict()), at: z.string(),
}).strict();
export type CodeReadSummary = z.infer<typeof CodeReadSummarySchema>;
/** "Code: 1,269 files, 37 MB read · 2,150 skipped: images, translations, design files" (the three biggest groups by files) */
export function codeReadLine(s: Pick<CodeReadSummary, "keptFiles" | "keptBytes" | "skippedFiles" | "skipped">): string {
  const read = `Code: ${filesWord(s.keptFiles)}, ${codeSize(s.keptBytes)} read`;
  if (!s.skippedFiles) return read;
  const groups = [...s.skipped].sort((a, b) => b.files - a.files).slice(0, 3).map((g) => g.reason);
  return `${read} · ${count(s.skippedFiles)} skipped${groups.length ? `: ${groups.join(", ")}` : ""}`;
}
